import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { ClipboardList, Plus, Search, X, Trash2, DollarSign } from 'lucide-react';
import { COMPANY } from '../../../config/company';

const EstimateLumpSumSection = ({
  language,
  lead,
  lumpSumItems,
  showLumpSumLibrary,
  setShowLumpSumLibrary,
  lumpSumSearchQuery,
  setLumpSumSearchQuery,
  filteredLumpSumTemplates,
  handleInputChange,
  handleLumpSumItemChange,
  addLumpSumItem,
  addAllLumpSumFromCategory,
  removeLumpSumItem,
  fetchLumpSumTemplates
}) => {
  return (
    <Card data-testid="estimate-lump-sum-section">
      <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center">
            <ClipboardList className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Alcance del Trabajo (Suma Global)' : 'Scope of Work (Lump Sum)'}
          </span>
          <div className="flex space-x-2">
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={() => { fetchLumpSumTemplates(); setShowLumpSumLibrary(true); }}
              data-testid="lump-sum-library-btn"
            >
              <Search className="h-4 w-4 mr-1" /> 
              {language === 'es' ? 'Biblioteca' : 'Library'}
            </Button>
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={() => addLumpSumItem()}
              data-testid="add-lump-sum-item-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> 
              {language === 'es' ? 'Agregar' : 'Add'}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Lump Sum Library Search */}
        {showLumpSumLibrary && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-blue-800">
                {language === 'es' ? 'Biblioteca de Alcance' : 'Scope Library'}
              </h4>
              <Button size="sm" variant="ghost" onClick={() => setShowLumpSumLibrary(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Input 
              placeholder={language === 'es' ? 'Buscar por palabra clave (ej: permiso, techo, plomería)...' : 'Search by keyword (e.g., permit, roof, plumbing)...'} 
              value={lumpSumSearchQuery} 
              onChange={(e) => setLumpSumSearchQuery(e.target.value)} 
              className="mb-4" 
              autoFocus 
              data-testid="lump-sum-search-input"
            />
            <div className="max-h-72 overflow-y-auto space-y-1">
              {filteredLumpSumTemplates.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  {language === 'es' ? 'Sin resultados. Intente otra búsqueda o seleccione categoría de proyecto.' : 'No results. Try another search or select a project category.'}
                </p>
              ) : (
                Object.entries(filteredLumpSumTemplates.reduce((acc, t) => { 
                  const cat = t.category || 'General'; 
                  if (!acc[cat]) acc[cat] = []; 
                  acc[cat].push(t); 
                  return acc; 
                }, {}))
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([category, items]) => (
                    <div key={category} className="mb-3">
                      <div className="px-3 py-1.5 text-sm font-semibold rounded-t sticky top-0 flex justify-between items-center bg-blue-100 text-blue-800">
                        <span>{category} ({items.length})</span>
                        <Button 
                          size="sm" 
                          className="bg-blue-600 hover:bg-blue-700 text-white h-6 text-xs px-2" 
                          onClick={() => addAllLumpSumFromCategory(items)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {language === 'es' ? 'Todos' : 'All'}
                        </Button>
                      </div>
                      {items.map((template) => (
                        <div 
                          key={template.id} 
                          className="p-2 bg-white border-x border-b hover:bg-blue-50 flex justify-between items-center"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium">{template.title || template.description}</div>
                            {template.project_name && (
                              <div className="text-xs text-blue-600">{template.project_name}</div>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 text-white ml-2 h-7 text-xs" 
                            onClick={() => addLumpSumItem({
                              ...template,
                              category: template.category,
                              description: template.title || template.description
                            })}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {language === 'es' ? 'Agregar' : 'Add'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        {/* Lump Sum Items Table */}
        {lumpSumItems.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            {language === 'es' ? 'Sin items. Agregue el alcance del trabajo.' : 'No items yet. Add scope of work.'}
          </p>
        ) : (
          <>
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 mb-2 text-xs font-semibold text-gray-500 border-b pb-2">
              <div className="col-span-4">{language === 'es' ? 'Categoría' : 'Category'}</div>
              <div className="col-span-7">{language === 'es' ? 'Descripción' : 'Description'}</div>
              <div className="col-span-1"></div>
            </div>
            {/* Items */}
            {lumpSumItems.map((item, index) => (
              <div 
                key={item.id} 
                className={`grid grid-cols-12 gap-2 py-2 items-center ${index % 2 === 0 ? 'bg-blue-50' : ''} rounded`}
                data-testid={`lump-sum-item-row-${index}`}
              >
                <div className="col-span-4">
                  <Input 
                    value={item.category || ''} 
                    onChange={(e) => handleLumpSumItemChange(item.id, 'category', e.target.value)} 
                    placeholder={language === 'es' ? 'Ej: Demolición' : 'e.g., Demolition Labor'}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-7">
                  <Input 
                    value={item.description || ''} 
                    onChange={(e) => handleLumpSumItemChange(item.id, 'description', e.target.value)} 
                    placeholder={language === 'es' ? 'Ej: Remover drywall mojado' : 'e.g., Tear out wet drywall'}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-1 text-right">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 h-8 w-8 p-0" 
                    onClick={() => removeLumpSumItem(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Add Item Button */}
        <div className="mt-4 pt-4 border-t flex justify-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => { fetchLumpSumTemplates(); setShowLumpSumLibrary(true); }} 
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="search-lump-sum-library-bottom-btn"
          >
            <Search className="h-4 w-4 mr-2" /> 
            {language === 'es' ? 'Buscar en Biblioteca' : 'Search Library'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => addLumpSumItem()}
            data-testid="add-lump-sum-item-bottom-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> 
            {language === 'es' ? 'Agregar Item' : 'Add Item'}
          </Button>
        </div>

        {/* Lump Sum Total */}
        <div className="mt-6 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-bold text-green-800">
                  {language === 'es' ? 'TOTAL DE PROPUESTA' : 'PROPOSAL TOTAL'}
                </p>
                <p className="text-xs text-green-600">
                  {language === 'es' ? 'Ingrese el monto total manualmente' : 'Enter the total amount manually'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-700">$</span>
              <Input 
                type="number" 
                value={lead.lump_sum_total || ''} 
                onChange={(e) => handleInputChange('lump_sum_total', parseFloat(e.target.value) || 0)} 
                placeholder="0.00"
                className="w-40 text-right text-xl font-bold border-green-400 focus:border-green-500"
                step="0.01"
                data-testid="lump-sum-total-input"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EstimateLumpSumSection;
